import { useReducer, useCallback } from 'react';
import {
  useBenefitDesignInputs,
  useBenefitDesignInputsActions,
} from '@/features/shop-insurance/stores/benefit-design-inputs-store';
import {
  useHiddenCarriers,
  useCarrierExclusionsActions,
} from '@/features/shop-insurance/stores/carrier-exclusions-store';
import type { BenefitDesignInputs } from '@/features/shop-insurance/types';

type SplitMode = 'combined' | 'separate';
type InflationTypeFilter = 'all' | 'simple' | 'compound';

export interface DraftState {
  benefit: string;
  durationType: SplitMode;
  duration: string;
  aobDuration: string;
  cobDuration: string;
  inflationType: SplitMode;
  inflation: string;
  aobInflation: string;
  cobInflation: string;
  inflationTypeFilter: InflationTypeFilter;
  draftHiddenCarriers: string[];
}

type DraftAction =
  | { type: 'SET_FIELD'; field: keyof DraftState; value: string }
  | { type: 'SET_DURATION_TYPE'; value: SplitMode }
  | { type: 'SET_INFLATION_TYPE'; value: SplitMode }
  | { type: 'SET_INFLATION_TYPE_FILTER'; value: InflationTypeFilter }
  | { type: 'TOGGLE_CARRIER'; carrier: string }
  | { type: 'RESET' };

function parseCurrency(raw: string): number | null {
  const n = Number(raw.replace(/[^0-9.]/g, ''));
  return isNaN(n) || n <= 0 ? null : n;
}

function parsePercent(raw: string): number | null {
  const stripped = raw.replace(/[^0-9.]/g, '');
  if (stripped === '') return null;
  const n = Number(stripped);
  return isNaN(n) || n < 0 || n > 5 ? null : n;
}

export function isLifetime(raw: string): boolean {
  return raw.trim().toLowerCase() === 'lifetime';
}

function formatDurationDraft(value: number): string {
  return value === Infinity ? 'Lifetime' : String(value);
}

function parseDuration(raw: string): number | null {
  if (isLifetime(raw)) return Infinity;
  const n = Number(raw.replace(/[^0-9.]/g, ''));
  return isNaN(n) || n <= 0 ? null : n;
}

function initializeDraft({
  inputs,
  hiddenCarriers,
}: {
  inputs: BenefitDesignInputs;
  hiddenCarriers: string[];
}): DraftState {
  return {
    benefit: inputs.monthlyBenefit !== null ? String(inputs.monthlyBenefit) : '',
    durationType: inputs.benefitDuration?.type ?? 'combined',
    duration: inputs.benefitDuration ? formatDurationDraft(inputs.benefitDuration.combined[0]) : '',
    aobDuration: inputs.benefitDuration ? formatDurationDraft(inputs.benefitDuration.aob[0]) : '',
    cobDuration: inputs.benefitDuration ? formatDurationDraft(inputs.benefitDuration.cob[0]) : '',
    inflationType: inputs.inflationProtection?.type ?? 'combined',
    inflation: inputs.inflationProtection ? String(inputs.inflationProtection.combined[0]) : '',
    aobInflation: inputs.inflationProtection ? String(inputs.inflationProtection.aob[0]) : '',
    cobInflation: inputs.inflationProtection ? String(inputs.inflationProtection.cob[0]) : '',
    inflationTypeFilter:
      inputs.inflationTypes === null
        ? 'all'
        : inputs.inflationTypes.includes('simple')
          ? 'simple'
          : 'compound',
    draftHiddenCarriers: hiddenCarriers,
  };
}

const EMPTY_DRAFT: DraftState = {
  benefit: '',
  durationType: 'combined',
  duration: '',
  aobDuration: '',
  cobDuration: '',
  inflationType: 'combined',
  inflation: '',
  aobInflation: '',
  cobInflation: '',
  inflationTypeFilter: 'all',
  draftHiddenCarriers: [],
};

function draftReducer(state: DraftState, action: DraftAction): DraftState {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_DURATION_TYPE':
      return { ...state, durationType: action.value };
    case 'SET_INFLATION_TYPE':
      return { ...state, inflationType: action.value };
    case 'SET_INFLATION_TYPE_FILTER':
      return { ...state, inflationTypeFilter: action.value };
    case 'TOGGLE_CARRIER': {
      const isHidden = state.draftHiddenCarriers.includes(action.carrier);
      return {
        ...state,
        draftHiddenCarriers: isHidden
          ? state.draftHiddenCarriers.filter(c => c !== action.carrier)
          : [...state.draftHiddenCarriers, action.carrier],
      };
    }
    case 'RESET':
      return { ...EMPTY_DRAFT };
    default:
      return state;
  }
}

export function useBenefitDesignDraftFilters() {
  const inputs = useBenefitDesignInputs();
  const hiddenCarriers = useHiddenCarriers();
  const {
    setMonthlyBenefit,
    setBenefitDuration,
    setInflationProtection,
    setInflationTypes,
    reset: resetStore,
  } = useBenefitDesignInputsActions();
  const { setHiddenCarriers, resetCarriers } = useCarrierExclusionsActions();

  const [draft, dispatch] = useReducer(
    draftReducer,
    { inputs, hiddenCarriers },
    initializeDraft,
  );

  const setField = useCallback(
    (field: keyof DraftState, value: string) => dispatch({ type: 'SET_FIELD', field, value }),
    [],
  );

  const setDurationType = useCallback(
    (value: SplitMode) => dispatch({ type: 'SET_DURATION_TYPE', value }),
    [],
  );

  const setInflationMode = useCallback(
    (value: SplitMode) => dispatch({ type: 'SET_INFLATION_TYPE', value }),
    [],
  );

  const setInflationTypeFilter = useCallback(
    (value: InflationTypeFilter) => dispatch({ type: 'SET_INFLATION_TYPE_FILTER', value }),
    [],
  );

  const toggleDraftCarrier = useCallback(
    (carrier: string) => dispatch({ type: 'TOGGLE_CARRIER', carrier }),
    [],
  );

  const handleApply = useCallback(() => {
    const benefit = parseCurrency(draft.benefit);
    setMonthlyBenefit(benefit);

    if (draft.durationType === 'combined') {
      const dur = parseDuration(draft.duration);
      setBenefitDuration(
        dur !== null
          ? { type: 'combined', combined: [dur, dur], aob: [dur, dur], cob: [dur, dur] }
          : null,
      );
    } else {
      const aob = parseDuration(draft.aobDuration);
      const cob = parseDuration(draft.cobDuration);
      if (aob !== null || cob !== null) {
        const aobRange: [number, number] = aob !== null ? [aob, aob] : [0, Infinity];
        const cobRange: [number, number] = cob !== null ? [cob, cob] : [0, Infinity];
        setBenefitDuration({
          type: 'separate',
          combined: [aobRange[0] + cobRange[0], aobRange[1] + cobRange[1]],
          aob: aobRange,
          cob: cobRange,
        });
      } else {
        setBenefitDuration(null);
      }
    }

    if (draft.inflationType === 'combined') {
      const inf = parsePercent(draft.inflation);
      setInflationProtection(
        inf !== null
          ? { type: 'combined', combined: [inf, inf], aob: [inf, inf], cob: [inf, inf] }
          : null,
      );
      setInflationTypes(draft.inflationTypeFilter === 'all' ? null : [draft.inflationTypeFilter]);
    } else {
      const aob = parsePercent(draft.aobInflation);
      const cob = parsePercent(draft.cobInflation);
      if (aob !== null || cob !== null) {
        const aobRange: [number, number] = aob !== null ? [aob, aob] : [0, 5];
        const cobRange: [number, number] = cob !== null ? [cob, cob] : [0, 5];
        setInflationProtection({
          type: 'separate',
          combined: [Math.min(aobRange[0], cobRange[0]), Math.max(aobRange[1], cobRange[1])],
          aob: aobRange,
          cob: cobRange,
        });
      } else {
        setInflationProtection(null);
      }
      setInflationTypes(null);
    }

    setHiddenCarriers(draft.draftHiddenCarriers);
  }, [draft, setMonthlyBenefit, setBenefitDuration, setInflationProtection, setInflationTypes, setHiddenCarriers]);

  const handleReset = useCallback(() => {
    dispatch({ type: 'RESET' });
    resetStore();
    resetCarriers();
  }, [resetStore, resetCarriers]);

  return {
    draft,
    setField,
    setDurationType,
    setInflationMode,
    setInflationTypeFilter,
    toggleDraftCarrier,
    handleApply,
    handleReset,
  };
}
